import React from 'react';
import {Card, Divider} from 'antd';

import EngageCurrency from '../EngageCurrency';
import {roundNumberUpToPrecision} from '../../../helpers/misc';
import {estimatedCostsText, totalCostsText} from '../../../helpers/text';
import {BookingCostBreakdownParams, CostsType, TaxType} from '../../../types/bookingTypes';
import {darkBlue} from '../../../assets/colors';

import './BookingCostBreakdown.less';

/*
* This component is imported into two places. CostBreakdown which is the child of TransactionHistory (table).
* And BookingDetails which shows an individual booking's update form and cost break down.
*
* Essentially, the transactions table may have multiple records pointing to the same booking, while booking details sums
* the transactions up and displays them all as one.
*
* The key difference between the following logic is determined by the transactionsTable param.
*
* If true we need to portion out totalCost from what transactionAmount actually is
* IE: totalCost = 10, transactionAmount = 5, then set a multiplier of 0.5 and determine time + distance costs by
* multiplying them by the multiplier.
*
* If false we don't do anything and refer to costs, totalCost, taxes, and totalTax.
*/
const BookingCostBreakDown = ({costs, totalCost, taxes, totalTax, isEstimatedCost, transactionsTable}: BookingCostBreakdownParams) => {
    let breakdownMessage = null;
    if (transactionsTable === true && costs.length !== 0) {
        // Append message to bottom of cost breakdowns in the transaction table, explaining the current breakdown may
        //  not add up to the transaction amount in the title row.
        breakdownMessage = "This cost reflects the aggregate of all the trip's transactions.";
    }

    // If costs are empty still display the labels followed by 0.00.
    if (costs.length === 0) {
        costs = [
            {
                type: 'time',
                amount: 0.00,
            },
            {
                type: 'distance',
                amount: 0.00,
            }
        ];
    }

    const getCostType = (cost:  CostsType) => {
        let type = 'charge';
        if (cost.amount < 0 || (cost.type.toLowerCase() === 'cancellation' && cost.amount === 0)) type = 'discount';
        if (cost.type.toLowerCase() === 'rate class') type = 'change';
        if (cost.type === 'open return') type = 'fee';
        return type;
    }

    return (
        <Card title="Cost Breakdown" className="booking-manage-details-card">
            <ul className="leaders">
                <li className="cost-breakdown-li-header">
                    <strong>Subtotal</strong>
                    <strong>
                        <EngageCurrency amount={totalCost}/>
                    </strong>
                </li>
                {costs.map((cost: CostsType) => (
                        <li className="cost-breakdown-li-subheader" key={cost.type}>
                            <span>{cost.type} {getCostType(cost)}</span>
                            <span><EngageCurrency amount={isNaN(cost.amount) ? 0 : cost.amount}/></span>
                        </li>
                    )
                )}
                <br/>
                <li className="cost-breakdown-li-header">
                    <strong>Taxes</strong>
                    <strong>
                        <EngageCurrency amount={totalTax}/>
                    </strong>
                </li>
                {taxes.map((tax: TaxType) => (
                        <li className="cost-breakdown-li-subheader" key={tax.name}>
                            <span>{`${tax.name} (${roundNumberUpToPrecision(tax.percent * 100, 2)}%)`}</span>
                            <span> <EngageCurrency amount={tax.amount}/></span>
                        </li>
                    )
                )}
                <Divider/>
                <li className="cost-breakdown-li-estimated">
                    <span style={{color: darkBlue}}>
                        {isEstimatedCost ? 'Estimated Cost' : 'Total Cost'}:
                    </span>
                    <span><EngageCurrency amount={totalCost + totalTax}/></span>
                </li>
            </ul>
            <p style={{fontSize: '12px'}}>
                {isEstimatedCost ?
                    estimatedCostsText : totalCostsText}
            </p>
            {breakdownMessage &&
                <p style={{fontSize: '12px', color: 'red'}}>
                    {breakdownMessage}
                </p>
            }
        </Card>
    );
};

export default BookingCostBreakDown;
